import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const BASE =
  'inline-flex items-center justify-center transition-all ease-out ' +
  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-0 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none disabled:translate-y-0';

// Each variant owns its own radius/weight/tracking so icon and compact sizes
// aren't forced into the big-CTA look shared by primary/secondary/ghost/danger.
const VARIANTS = {
  primary:
    'rounded-xl font-semibold tracking-wide duration-200 bg-gradient-to-r from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-500/30 hover:shadow-xl hover:shadow-brand-500/40 hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-brand-400',
  secondary:
    'rounded-xl font-semibold tracking-wide duration-200 bg-white text-brand-700 border border-slate-200 hover:border-brand-300 hover:bg-brand-50 focus-visible:ring-brand-300',
  ghost:
    'rounded-xl font-semibold tracking-wide duration-200 bg-transparent text-slate-600 hover:bg-slate-100 focus-visible:ring-slate-300',
  danger:
    'rounded-xl font-semibold tracking-wide duration-200 bg-gradient-to-r from-accent-500 to-accent-700 text-white shadow-lg shadow-accent-500/30 hover:shadow-xl hover:-translate-y-0.5 focus-visible:ring-accent-400',
  // Compact, flat CTA — the "+ Add X" buttons above every list page.
  solid:
    'rounded-md font-medium duration-150 bg-brand-500 text-white hover:bg-brand-600 active:scale-95 focus-visible:ring-brand-300',
  // Compact, flat neutral — secondary form actions like "Cancel".
  outline:
    'rounded-md font-medium duration-150 bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 active:scale-95 focus-visible:ring-slate-200',
  // Compact, flat accent — filter "Reset" buttons.
  accent:
    'rounded-md font-medium duration-150 bg-accent-500 text-white hover:bg-accent-600 active:scale-95 focus-visible:ring-accent-300',
  // Icon-only square button — the "back" action on form/detail pages.
  icon:
    'rounded-md duration-150 bg-[#efefef] border border-[#e5e7eb] text-slate-600 hover:bg-slate-200 active:scale-95 focus-visible:ring-slate-300',
  // Icon-only square button — the "edit" (pencil) action in DataTable rows.
  'icon-edit':
    'rounded-md duration-150 bg-brand-100 border border-brand-200 text-brand-600 hover:bg-brand-200 active:scale-95 focus-visible:ring-brand-300',
  // Icon-only square button — the "print" action in DataTable rows. Same soft two-tone
  // language as icon-edit, in sky instead of brand so it reads as a distinct action.
  'icon-print':
    'rounded-md duration-150 bg-sky-100 border border-sky-200 text-sky-600 hover:bg-sky-200 active:scale-95 focus-visible:ring-sky-300',
  'icon-brand':
    'rounded-md duration-150 bg-brand-500 border border-brand-600 text-white hover:bg-brand-600 active:scale-95 focus-visible:ring-brand-300',
  'icon-danger':
    'rounded-md duration-150 bg-accent-500 border border-accent-400 text-white hover:bg-accent-600 active:scale-95 focus-visible:ring-accent-300',
};

const SIZES = {
  sm: 'gap-2 px-4 py-2 text-xs',
  md: 'gap-2 px-5 py-3 text-sm',
  lg: 'gap-2 px-6 py-3.5 text-base',
  compact: 'gap-1.5 px-3 py-2 text-xs',
  icon: 'h-[26px] w-[26px] p-0',
};

/**
 * Reusable button with built-in loading state. Renders a spinner and disables
 * itself while `loading` is true, so callers don't need to manage that separately.
 *
 * Pass `to` to render a react-router `Link` (for "+ Add X" style navigation
 * buttons) instead of a `<button>` — everything else about the API is the same.
 */
export function Button({
  children,
  to,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  type = 'button',
  className = '',
  ...props
}) {
  const classes = [
    BASE,
    VARIANTS[variant],
    SIZES[size],
    fullWidth ? 'w-full' : '',
    className,
  ].join(' ');

  const content = (
    <>
      {loading && <Loader2 size={size === 'icon' ? 14 : 18} className="animate-spin" />}
      {children}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} {...props}>
        {content}
      </Link>
    );
  }

  return (
    <button type={type} disabled={disabled || loading} className={classes} {...props}>
      {content}
    </button>
  );
}

export default Button;
